/*
 * Copyright 2026 Sony Corporation
 */

import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import type { Device, Optics } from '@ips-device-verse/twin-service/schema';

import { useCreativeLook } from '../../hooks/use-creative-look';
import { aspectOf, type ResolvedOptics } from '../../scene/camera-optics';
import type { Transform } from '../../scene/types';
import { DevicePosePanel } from './DevicePosePanel';
import { CameraPreview } from './CameraPreview';
import { CreativeLookPanel } from './CreativeLookPanel';
import { LensPanel, SensorPanel } from './OpticsPanel';
import { PanelSection } from './PanelSection';

export interface DeviceInspectorOptics {
  value: ResolvedOptics;
  onChange: (change: Partial<Optics>) => void;
  pending?: boolean;
  error?: string;
  depthOfFieldShown: boolean;
  onDepthOfFieldShownChange: (shown: boolean) => void;
}

export interface DeviceInspectorProps {
  device: Device | undefined;
  pose: Transform | undefined;
  onPoseChange?: (transform: Transform) => void;
  look: ReturnType<typeof useCreativeLook>;
  optics: DeviceInspectorOptics | undefined;
  previewCanvasRef: (canvas: HTMLCanvasElement | null) => void;
  onDelete: (deviceId: string) => void;
}

const Field = ({ label, value }: { label: string; value: string }) => (
  <Box className="flex items-baseline justify-between gap-2">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" className="font-mono">
      {value}
    </Typography>
  </Box>
);

/**
 * Two different facts, and conflating them reads as a broken device.
 *
 * `reporting` is whether a gateway has this device at all. `connected` is
 * whether a PTP client is holding it — a device nobody has opened is idle, not
 * offline.
 */
const statusOf = (reporting: boolean, connected: boolean | undefined, client: string | undefined) => {
  if (!reporting) {
    return { label: 'No gateway', colour: 'default' as const };
  }
  return connected
    ? { label: client ? `In use · ${client}` : 'In use', colour: 'success' as const }
    : { label: 'Idle', colour: 'default' as const };
};

/**
 * The selected device, in the right-hand panel.
 *
 * It shares that column with the chat and is styled to match: no surface of its
 * own, because the panel is the surface.
 */
export const DeviceInspector = ({
  device,
  pose,
  onPoseChange,
  look,
  optics,
  previewCanvasRef,
  onDelete,
}: DeviceInspectorProps) => {
  if (!device) {
    return (
      <Box className="flex min-h-0 flex-1 items-center justify-center px-6 text-center">
        <Typography variant="body2" color="text.secondary">
          Select a device to see its details.
        </Typography>
      </Box>
    );
  }

  const status = statusOf(look.reporting, look.connected, look.client);

  return (
    <Box className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 pb-3">
      {/* Bleeds past the panel padding so the preview gets the full column width. */}
      <Box className="-mx-3 shrink-0">
        <CameraPreview canvasRef={previewCanvasRef} active aspect={optics ? aspectOf(optics.value) : undefined} />
      </Box>

      <Box className="flex items-center justify-between gap-2">
        <Typography variant="subtitle1">{device.name}</Typography>
        <Chip size="small" label={status.label} color={status.colour} variant="outlined" />
      </Box>

      {look.loadError && (
        <Alert severity="error" action={<Button onClick={look.reload}>Retry</Button>}>
          {look.loadError.message}
        </Alert>
      )}

      <Box>
        <PanelSection title="Info">
          <Stack spacing={1}>
            <Field label="Model" value={device.model} />
            <Field label="Serial" value={device.serial} />
            <Field label="Address" value={`${device.address}:15740`} />
            <Field label="Thing" value={device.thing_name} />
          </Stack>
        </PanelSection>

        {optics && (
          <>
            <PanelSection title="Lens">
              <LensPanel
                optics={optics.value}
                onChange={optics.onChange}
                pending={optics.pending}
                error={optics.error}
                depthOfFieldShown={optics.depthOfFieldShown}
                onDepthOfFieldShownChange={optics.onDepthOfFieldShownChange}
              />
            </PanelSection>

            <PanelSection title="Sensor">
              <SensorPanel
                optics={optics.value}
                onChange={optics.onChange}
                pending={optics.pending}
                error={optics.error}
              />
            </PanelSection>
          </>
        )}

        <PanelSection title="Transform">
          <DevicePosePanel transform={pose} onChange={onPoseChange} />
        </PanelSection>

        <PanelSection title="Creative Look">
          {/* Only while no gateway has this device. With one running the write
              is applied as soon as it lands, whether a client is attached or not. */}
          {!look.reporting && (
            <Typography variant="caption" color="text.secondary">
              Changes are stored now and applied when a gateway picks this device up.
            </Typography>
          )}
          <CreativeLookPanel look={look} />
        </PanelSection>
      </Box>

      <Box className="flex-1" />
      <Button color="error" size="small" onClick={() => onDelete(device.device_id)}>
        Remove device
      </Button>
    </Box>
  );
};
